import { useState, useCallback, useRef, useEffect } from "react";
import axios from "../../../config/axios";
import useIdleState from "./useIdleState";
import WakeOverlay from "./WakeOverlay";
import TransmittingOverlay from "./TransmittingOverlay";
import BeaconSubCheck from "./BeaconSubCheck";
import BeaconPairingCode from "./BeaconPairingCode";
import DesignStudio from "./DesignStudio";
import BeaconDemo from "./BeaconDemo";
import BandHowTo from "./BandHowTo";
import { useConfig } from "../../../hooks/useConfig";
import { useFairyFlightControls } from "../../../hooks/useFairyFlight";
import useBeaconControl from "../../../hooks/useBeaconControl";
import { useRemote } from "../../../hooks/useRemote";
import useGlobalStore from "../../../store/global.store";
import "./MagicBand.css";
// LIVE Show Sync picker GIFs (put the files in public/static/)
const LSS_MULTIVIEW_MP4 = "/static/lss-multiview.jpg";
const LSS_CLOSEUP_MP4 = "/static/lss-closeup.jpg";

const IDLE_THRESHOLD_MS = 30 * 60 * 1000;
const BG_WAKE_TO_CANCEL_MS = 2000;
const TX_STEP_MS = 500;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// DEV ONLY: localStorage wsDevBandPlus = '1' skips the pairing screen so the
// Band+ UI can be tested on a unit with no Beacon. No commands reach a band.
const devBandBypass = () => { try { return localStorage.getItem("wsDevBandPlus") === "1"; } catch (e) { return false; } };

const postWakeSlow = (deviceId) =>
  axios.post("/esp/beacon-show", { deviceId, fx: "WAKE_UP_SLOW", vib: "OFF" }, { withCredentials: true });

const postWakeFast = (deviceId) =>
  axios.post("/esp/beacon-show", { deviceId, fx: "WAKE_UP_FAST", vib: "OFF" }, { withCredentials: true });

const postCancelRaw = (deviceId) =>
  axios.post("/esp/cancel", { deviceId }, { withCredentials: true });

const buildColorPayload = (mode, colors) => {
  if (mode === "1color") {
    return { action: "preset", args: { color: colors.outer }, vib: 0 };
  }
  if (mode === "2color") {
    return { action: "dual", args: { inner: colors.outer, outer: colors.inner }, vib: 0 };
  }
  if (mode === "5color") {
    return {
      action: "rainbow",
      args: { tl: colors.c, tr: colors.tr, c: colors.tl, bl: colors.br, br: colors.bl },
      vib: 0,
    };
  }
  return null;
};

const MagicBand = ({ device, onDeviceRefetch }) => {
  const {
    enabled: fairyEnabled,
    vibOn: fairyVib,
    toggleEnabled: toggleFairy,
    toggleVib: toggleFairyVib,
  } = useFairyFlightControls();

  const {
    isController,
    hasHolder,
    holder,
    hasAddon: hasBeaconAddon,
    synced,
    keyReady,
    claim,
    release,
  } = useBeaconControl();
  const { updateView } = useRemote();
  const hasVipAddOn = useGlobalStore((state) => state.hasVipAddOn);
    const { config } = useConfig();
  const [showDemo, setShowDemo] = useState(false);
    const [lssPopup, setLssPopup] = useState(null); // null | "upgrade" | "pick"

    // MagicBeacon Tour: opens by itself ONCE (first time Band+ loads with a
  // paired beacon), after that only from the How To button.
  const [showHowTo, setShowHowTo] = useState(false);
  const bandReady = !!device?.magic_beacon_device || devBandBypass();
  useEffect(() => {
    if (!bandReady) return;
    try {
      if (localStorage.getItem("wsBandHowToSeen") === "1") return;
      localStorage.setItem("wsBandHowToSeen", "1");
    } catch (e) {}
    setShowHowTo(true);
  }, [bandReady]);

  // Release flag: config/Buttons { Demo: bool, Demo_Override: [subIds] } —
  // same pattern as BottomBar's buttonEnabled. Absent node/flag = hidden.
  const demoEnabled = (() => {
    const b = config?.Buttons;
    if (!b) return false;
    if (b.Demo === true) return true;
    const ov = b.Demo_Override;
    const id = device?.subscription?._id;
    if (!id) return false;
    if (Array.isArray(ov)) return ov.includes(id);
    if (typeof ov === "string") return ov === id;
    return false;
  })();

  const [soleHolderMsg, setSoleHolderMsg] = useState(false);
  const [tinkNoticeMsg, setTinkNoticeMsg] = useState(false);
  const tinkNoticeTimerRef = useRef(null);
  const [pendingTransmit, setPendingTransmit] = useState(null);
  const [pendingTinkOn, setPendingTinkOn] = useState(null); // truthy = tink take-control modal up

// Auto-off: Tink is on but ANOTHER device holds control → turn it off
// (it lost control anyway; the saved setting becomes "off"). Gated on
// synced + keyReady so a booting device — whose isController is false
// until identity loads — never wipes its saved on-state spuriously.
useEffect(() => {
  if (fairyEnabled && synced && keyReady && hasHolder && !isController) {
    toggleFairy(); // → off, persisted by the hook
    clearTimeout(tinkNoticeTimerRef.current);
    setTinkNoticeMsg(false);
  }
}, [fairyEnabled, synced, keyReady, hasHolder, isController, toggleFairy]);

  const magic_beacon_device_id = device?.magic_beacon_device?._id;
  const beaconDeviceCount = device?.beacon_device_count ?? 2;

  const deviceIdRef = useRef(magic_beacon_device_id);
  useEffect(() => {
    deviceIdRef.current = magic_beacon_device_id;
  }, [magic_beacon_device_id]);

  /* ====== Auto-claim on first Band+ entry ======
   * The `device?.cf_device_name` gate prevents the first-load race where
   * the auto-claim could write fallback labels ("(no name)", "Display")
   * before the /device/:id payload had fully hydrated into the store.
   */
  useEffect(() => {
    if (
      synced &&
      hasBeaconAddon &&
      !hasHolder &&
      !isController &&
      device?.cf_device_name
    ) {
      claim();
    }
  }, [synced, hasBeaconAddon, hasHolder, isController, claim, device?.cf_device_name]);

  /* Tink Tracker: vibration is always on — no user toggle anymore */
  useEffect(() => {
    if (fairyEnabled && !fairyVib) {
      toggleFairyVib();
    }
  }, [fairyEnabled, fairyVib, toggleFairyVib]);
  const handleTinkToggle = () => {
  const turningOn = !fairyEnabled;
  // Turning ON without control → ask first, exactly like colors.
  if (turningOn && synced && hasHolder && !isController) {
    setPendingTinkOn(true);
    return;
  }
  toggleFairy();
  clearTimeout(tinkNoticeTimerRef.current);
  if (turningOn) {
    setTinkNoticeMsg(true);
    tinkNoticeTimerRef.current = setTimeout(() => setTinkNoticeMsg(false), 4000);
  } else {
    setTinkNoticeMsg(false);
  }
};

const confirmTinkTakeControl = async () => {
  setPendingTinkOn(null);
  const r = await claim();
  if (!r.ok) {
    console.error("Tink take-control claim failed:", r.reason);
    return;
  }
  toggleFairy(); // on + persisted
  clearTimeout(tinkNoticeTimerRef.current);
  setTinkNoticeMsg(true);
  tinkNoticeTimerRef.current = setTimeout(() => setTinkNoticeMsg(false), 4000);
};

const cancelTinkTakeControl = () => setPendingTinkOn(null);

  const [overlay, setOverlay] = useState(null);
  const pendingRef = useRef(false);
  const cancelInFlightRef = useRef(false);
  const queuedCommandRef = useRef(null);
  const bgWakeInProgressRef = useRef(false);
  const transmitAbortRef = useRef(false);
  const [transmitting, setTransmitting] = useState(false);
  const wakeFastIntervalRef = useRef(null);
  const containerRef = useRef(null);
  const designRef = useRef(null);
  const lastActivityRef = useRef(0);

  const handleTick = useCallback(() => {
    if (pendingRef.current) pendingRef.current = false;
  }, []);

  useIdleState(magic_beacon_device_id, handleTick);

  const closeOverlay = useCallback(() => setOverlay(null), []);

  const fireBackgroundWake = useCallback(async () => {
    if (!isController) return;
    const id = deviceIdRef.current;
    if (!id) return;
    bgWakeInProgressRef.current = true;
    try {
      await postWakeSlow(id);
      await sleep(BG_WAKE_TO_CANCEL_MS);
      if (bgWakeInProgressRef.current) {
        await postCancelRaw(id);
      }
    } catch (e) {
      console.error("Background wake failed:", e);
    } finally {
      bgWakeInProgressRef.current = false;
    }
  }, [isController]);

  const handleActivity = useCallback(() => {
    if (!deviceIdRef.current) return;
    const now = Date.now();
    const idleTime = now - lastActivityRef.current;
    lastActivityRef.current = now;
    if (idleTime > IDLE_THRESHOLD_MS) {
      fireBackgroundWake();
    }
  }, [fireBackgroundWake]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const events = ["click", "touchstart", "keydown"];
    events.forEach((e) => el.addEventListener(e, handleActivity, { passive: true }));
    return () => events.forEach((e) => el.removeEventListener(e, handleActivity));
  }, [handleActivity, magic_beacon_device_id]);

  useEffect(() => {
    return () => {
      if (wakeFastIntervalRef.current) {
        clearInterval(wakeFastIntervalRef.current);
        wakeFastIntervalRef.current = null;
      }
    };
  }, []);

  const handleToggleBeaconControl = async () => {
    if (isController) {
      const r = await release();
      if (r.reason === "sole_holder") {
        setSoleHolderMsg(true);
        setTimeout(() => setSoleHolderMsg(false), 4000);
      }
    } else {
      await claim();
    }
  };
  /* LIVE Show Sync — one entry point to the VIP door selector, cloned from
     BottomBar.handleVipClick (holder → "VIP", else upgrade). */
    const handleVipLiveShowSync = () => {
    setLssPopup(hasVipAddOn ? "pick" : "upgrade");
  };
  const closeLss = () => setLssPopup(null);
  const goLssUpgrade = () => { setLssPopup(null); updateView("VIP__upgrade"); };
  // Same calls as VipBottomBar: door 3 = MultiView, door 2 = Close-Up selector.
  const goLssMultiview = () => { setLssPopup(null); updateView("VIP__multiview"); };
  const goLssCloseup = () => { setLssPopup(null); updateView("VIP_door_2_selector", () => {}); };

  if (!device?.magic_beacon_device && !devBandBypass()) {
    return (
      <div className="mb-container">
        <BeaconSubCheck />
        {hasBeaconAddon ? (
          <BeaconPairingCode
            displayDeviceId={device?._id}
            onPaired={onDeviceRefetch}
          />
        ) : (
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            height: "100%", padding: "40px", textAlign: "center",
          }}>
            <h2 style={{ fontFamily: "Lato", fontSize: "28px", color: "#ccc", marginBottom: "12px" }}>
              Magic Beacon Device not yet setup
            </h2>
            <p style={{ fontFamily: "Lato", fontSize: "18px", color: "#999" }}>
              Please contact support to register your Magic Beacon device.
            </p>
          </div>
        )}
      </div>
    );
  }

  const postCommand = (payload) => {
    const finalPayload = { ...payload, deviceId: magic_beacon_device_id };
    if (cancelInFlightRef.current) {
      queuedCommandRef.current = finalPayload;
      return;
    }
    pendingRef.current = true;
    axios.post("/esp/command", finalPayload, { withCredentials: true }).catch(console.error);
  };

  const postCancel = async () => {
    cancelInFlightRef.current = true;
    try {
      await postCancelRaw(magic_beacon_device_id);
    } catch (e) {
      console.error(e);
    } finally {
      cancelInFlightRef.current = false;
      const queued = queuedCommandRef.current;
      if (queued) {
        queuedCommandRef.current = null;
        pendingRef.current = true;
        axios.post("/esp/command", queued, { withCredentials: true }).catch(console.error);
      }
    }
  };

  const handleCancelFromOverlay = () => {
    transmitAbortRef.current = true;
    setOverlay(null);
    postCancel();
  };

  const startWakeFastLoop = () => {
    if (wakeFastIntervalRef.current) return;
    const id = deviceIdRef.current;
    if (!id) return;
    postWakeFast(id).catch(console.error);
    wakeFastIntervalRef.current = setInterval(() => {
      const did = deviceIdRef.current;
      if (did) postWakeFast(did).catch(console.error);
    }, 3000);
  };

  const stopWakeFastLoop = () => {
    if (wakeFastIntervalRef.current) {
      clearInterval(wakeFastIntervalRef.current);
      wakeFastIntervalRef.current = null;
    }
  };

  const handlePrepare = () => {
    if (!isController) return;
    bgWakeInProgressRef.current = false;
    transmitAbortRef.current = false;
    startWakeFastLoop();
  };

  const runTransmit = async ({ mode, colors }) => {
    if (transmitting) return;
    stopWakeFastLoop();
    transmitAbortRef.current = false;
    setTransmitting(true);
    const id = magic_beacon_device_id;
    const aborted = () => transmitAbortRef.current;
    try {
      await postCancelRaw(id);
      await sleep(TX_STEP_MS); if (aborted()) return;
      const payload = buildColorPayload(mode, colors);
      if (payload) {
        await axios.post("/esp/command", { ...payload, deviceId: id }, { withCredentials: true });
      }
    } catch (e) {
      console.error("Transmit sequence failed:", e);
    } finally {
      setTransmitting(false);
    }
  };

  const handleTransmit = async ({ mode, colors }) => {
  // Returns a verdict for the wizard: "sent" → it runs the countdown;
  // "pending" → take-control modal is up, NO countdown yet;
  // "failed" → nothing sent, wizard returns to selection.
  // runTransmit is deliberately NOT awaited — it runs across the countdown.
  if (transmitting) return "failed";
  if (devBandBypass() && !magic_beacon_device_id) return "sent"; // DEV: fake success so the countdown UI can be tested
  if (isController) {
    runTransmit({ mode, colors });
    return "sent";
  }
  if (hasHolder && holder?.holderDeviceId !== device?._id) {
    setPendingTransmit({ mode, colors });
    return "pending";
  }
  const r = await claim();
  if (r.ok) {
    runTransmit({ mode, colors });
    return "sent";
  }
  console.error("Auto-claim during transmit failed:", r.reason);
  return "failed";
};

  const confirmHandoffAndTransmit = async () => {
    const params = pendingTransmit;
    setPendingTransmit(null);
    if (!params) return;
    const r = await claim();
    if (!r.ok) {
      console.error("Handoff claim failed:", r.reason);
      stopWakeFastLoop();
      if (magic_beacon_device_id) postCancelRaw(magic_beacon_device_id).catch(console.error);
      return;
    }
    designRef.current?.beginTransmitCountdown?.();
  runTransmit(params);
};

  const cancelHandoff = () => {
    setPendingTransmit(null);
    stopWakeFastLoop();
    if (magic_beacon_device_id) postCancelRaw(magic_beacon_device_id).catch(console.error);
  };

  const handleMagicCancel = () => {
    if (!isController) return;
    transmitAbortRef.current = true;
    stopWakeFastLoop();
    if (magic_beacon_device_id) postCancelRaw(magic_beacon_device_id).catch(console.error);
  };

  /* ====== Beacon Control button visibility ======
   * Same rule as before: only render if more than one device on this
   * account has the beacon addon. Solo households see nothing — the
   * auto-claim still happens silently so they always have the lock when
   * VIP / multiview / Fairy's Flight need it.
   */
  const showBeaconButton = hasBeaconAddon && synced && beaconDeviceCount > 1;
  const otherHolderText =
    hasHolder && holder?.holderDeviceId !== device?._id
      ? `Your Wait Time ${holder.holderDeviceType} (${holder.holderLabel}): has control of your Magic Beacon. Tap Beacon Control button to switch.`
      : null;

  return (
    <div className="mb-container" ref={containerRef}>
      <style>{`
        @keyframes beacon-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .mb-tab.beacon-tab {
          color: #ffffff !important;
          font-weight: 700;
          letter-spacing: 0.3px;
          border: none;
          text-shadow: 0 1px 1px rgba(0,0,0,0.25);
          transition: filter 0.15s ease, transform 0.08s ease;
        }
        .mb-tab.beacon-tab:hover { filter: brightness(1.06); }
        .mb-tab.beacon-tab:active { transform: translateY(1px); }
        .mb-tab.beacon-tab-on {
          background: linear-gradient(
            110deg,
            #059669 0%,
            #10b981 50%,
            #059669 100%
          ) !important;
          background-size: 220% 100% !important;
          animation: beacon-shimmer 10s linear infinite;
          box-shadow: 0 2px 8px rgba(5,150,105,0.40),
                      inset 0 1px 0 rgba(255,255,255,0.18);
        }
        .mb-tab.beacon-tab-off {
          background: linear-gradient(180deg, #dc2626 0%, #991b1b 100%) !important;
          box-shadow: 0 2px 8px rgba(153,27,27,0.40),
                      inset 0 1px 0 rgba(255,255,255,0.15);
        }
                .mb-tab.demo-tab {
          color: #ffffff !important;
          font-weight: 700;
          border: none;
          background: linear-gradient(180deg, #7c3aed 0%, #5b21b6 100%) !important;
          box-shadow: 0 2px 8px rgba(91,33,182,0.40),
                      inset 0 1px 0 rgba(255,255,255,0.18);
        }
        .mb-tab.demo-tab:hover { filter: brightness(1.08); }
        /* VIP Live Show Sync: purple→pink moving OUTLINE + glow + red LIVE dot.
           Transparent border + 2 bg layers: opaque inner (padding-box) over
           scrolling purple/pink (border-box). background-image + !important
           defends against .mb-tab:hover's background shorthand in MagicBand.css.
           No font-size/padding override → inherits .mb-tab base so height
           matches its neighbors exactly. */
        .mb-tab.vip-sync-tab {
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          white-space: nowrap;
          color: #fff !important;
          text-shadow: 0 1px 4px rgba(0,0,0,0.5);
          border: 3px solid transparent;
                    background-image:
            linear-gradient(rgba(8,10,24,.35), rgba(8,10,24,.35)),
            url(/beacon-promo/night.jpg),
            linear-gradient(90deg, #FF5C93, #FF8A3D, #FFD23D, #3EE08A, #3CC9FF, #8F7CFF, #FF5C93, #FF8A3D, #FFD23D, #3EE08A, #3CC9FF, #8F7CFF, #FF5C93) !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.35);
          background-origin: border-box !important;
          background-clip: padding-box, padding-box, border-box !important;
          background-size: auto, cover, 300% 100% !important;
          background-repeat: no-repeat, no-repeat, repeat !important;
          animation: mbVipEdge 7s linear infinite;
        }
        .mb-tab.vip-sync-tab:hover {
          filter: brightness(1.1);
        }
                @keyframes mbVipEdge {
          0%   { background-position: 0 0, center, 0% 50%; }
          100% { background-position: 0 0, center, 300% 50%; }
        }
                .mb-tab.howto-tab {
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          white-space: nowrap;
          border: none;
          color: #007bff !important;
          background: #ffffff !important;
          font-weight: 900;
        }
        .mb-tab.howto-tab:active { transform: scale(.96); }
        .howto-q {
          flex: 0 0 auto;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #007bff;
          color: #fff;
          font-size: 15px;
          font-weight: 900;
          line-height: 22px;
          text-align: center;
        }
        
        .mb-live-dot {
          flex: 0 0 auto;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #ff2d2d;
          box-shadow: 0 0 6px rgba(255, 45, 45, 0.85);
          animation: mbLiveDotPulse 1.6s ease-in-out infinite;
        }
        @keyframes mbLiveDotPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.45; transform: scale(0.75); }
        }
        .beacon-inactive-strip {
          padding: 12px 18px;
          background: linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          font-family: Lato, sans-serif;
          font-size: 15px;
          font-weight: 600;
          text-align: center;
          border-top: 1px solid #fecaca;
          border-bottom: 1px solid #fecaca;
        }
        .tink-alert-strip {
  padding: 12px 18px;
  background: linear-gradient(180deg, #f0fdf4 0%, #dcfce7 100%);
  color: #166534;
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  border-top: 1px solid #bbf7d0;
  border-bottom: 1px solid #bbf7d0;
}
        .beacon-sole-strip {
          padding: 12px 18px;
          background: #fef3c7;
          color: #92400e;
          font-family: Lato, sans-serif;
          font-size: 15px;
          font-weight: 600;
          text-align: center;
          border-top: 1px solid #fde68a;
          border-bottom: 1px solid #fde68a;
        }
        .beacon-handoff-backdrop {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.6);
          display: flex; align-items: center; justify-content: center;
          z-index: 10000;
        }
        .beacon-handoff-modal {
          background: white;
          padding: 28px 24px 22px;
          border-radius: 12px;
          max-width: 440px;
          width: calc(100% - 32px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.45);
          font-family: Lato, sans-serif;
          text-align: center;
        }
        .beacon-handoff-modal h3 {
          margin: 0 0 12px;
          font-size: 22px;
          color: #1f2937;
        }
        .beacon-handoff-modal p {
          margin: 0 0 22px;
          font-size: 16px;
          color: #4b5563;
          line-height: 1.4;
        }
        .beacon-handoff-buttons {
          display: flex; gap: 12px; justify-content: center;
        }
        .beacon-handoff-yes, .beacon-handoff-no {
          flex: 1; padding: 12px 20px;
          border: none; border-radius: 8px;
          font-family: Lato, sans-serif;
          font-size: 16px; font-weight: 600;
          cursor: pointer;
        }
        .beacon-handoff-yes { background: #10b981; color: white; }
        .beacon-handoff-no  { background: #e5e7eb; color: #374151; }
                .lss-back{position:fixed;inset:0;background:rgba(6,7,16,.82);display:flex;align-items:center;justify-content:center;z-index:10000;}
        .lss-card{border:4px solid transparent;border-radius:22px;padding:30px 34px 22px;text-align:center;font-family:Lato,sans-serif;color:#fff;box-shadow:0 16px 50px rgba(0,0,0,.6);background-image:linear-gradient(rgba(8,10,24,.55),rgba(8,10,24,.55)),url(/beacon-promo/night.jpg),linear-gradient(90deg,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF,#FF5C93);background-origin:border-box;background-clip:padding-box,padding-box,border-box;background-size:auto,cover,300% 100%;background-position:center,center,0% 50%;background-repeat:no-repeat,no-repeat,repeat;animation:lssRing 7s linear infinite;}
        @keyframes lssRing{from{background-position:center,center,0% 50%}to{background-position:center,center,300% 50%}}        .lss-title{display:flex;align-items:center;justify-content:center;gap:12px;margin:0 0 10px;font-size:34px;font-weight:900;color:#fff;}
        .lss-sub{margin:0 0 24px;font-size:20px;font-weight:600;color:rgba(255,255,255,.88);line-height:1.4;}
        .lss-upgrade{display:block;width:100%;padding:18px 24px;border:3px solid #fff;border-radius:14px;background:rgba(255,255,255,.12);color:#fff;font-family:Lato,sans-serif;font-size:26px;font-weight:900;letter-spacing:1px;cursor:pointer;}
        .lss-upgrade:active,.lss-opt:active{transform:scale(.97);}
        .lss-no{margin-top:16px;padding:8px 16px;border:none;background:none;color:rgba(255,255,255,.75);font-family:Lato,sans-serif;font-size:18px;font-weight:700;text-decoration:underline;cursor:pointer;}
        .lss-close{display:block;margin:22px auto 0;padding:14px 56px;border:2px solid rgba(255,255,255,.55);border-radius:12px;background:rgba(255,255,255,.12);color:#fff;font-family:Lato,sans-serif;font-size:20px;font-weight:900;letter-spacing:1px;cursor:pointer;}
        .lss-close:active{transform:scale(.97);}
        .lss-opts{display:flex;gap:22px;}
        .lss-opt{width:420px;padding:0;border:3px solid rgba(255,255,255,.3);border-radius:16px;background:#12051f;overflow:hidden;cursor:pointer;color:#fff;font-family:Lato,sans-serif;text-align:center;transition:transform .1s ease;}
        .lss-gif{display:block;width:100%;aspect-ratio:16/9;object-fit:cover;background:#000;}
        .lss-opt-name{display:block;padding:14px 12px 4px;font-size:24px;font-weight:900;}
        .lss-opt-desc{display:block;padding:0 14px 16px;font-size:17px;font-weight:600;color:rgba(255,255,255,.82);line-height:1.35;}
      `}</style>

      <BeaconSubCheck />

      {overlay === "wake" && <WakeOverlay onClose={closeOverlay} />}
      {overlay === "transmit" && (
        <TransmittingOverlay onCancel={handleCancelFromOverlay} onClose={closeOverlay} />
      )}

      <div className="mb-tabs" style={showDemo ? { display: "none" } : undefined}>
        <div className="mb-tab-row">
          {showBeaconButton ? (
            <button
              className={`mb-tab beacon-tab ${isController ? "beacon-tab-on" : "beacon-tab-off"}`}
              onClick={handleToggleBeaconControl}
            >
              Beacon Control: {isController ? "Active" : "Inactive"}
            </button>
          ) : null}
          
          <div className={`tink-tracker ${fairyEnabled ? "on" : ""}`}>
  <button className="tink-toggle" onClick={handleTinkToggle}>
    <svg className="tink-bell" viewBox="0 0 24 24" width="22" height="22"
         fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
    Tink Alert
    <span className="tink-switch"><span className="tink-knob" /></span>
  </button>
</div>
          <button className="mb-tab vip-sync-tab" onClick={handleVipLiveShowSync}>
            <span className="mb-live-dot" />
            VIP Live Show Sync
          </button>

                    <button className="mb-tab howto-tab" onClick={() => setShowHowTo(true)}>
            <span className="howto-q">?</span>
            How To
          </button>
                    {demoEnabled && (
            <button className="mb-tab demo-tab" onClick={() => setShowDemo(true)}>
              SHOW DEMO
            </button>
          )}
        </div>
      </div>

            {/* Status strips — wrapped so the Timer's own CSS can float them
          (overlay) while it's mounted; everywhere else they push as normal. */}
      <div className="mb-strips">
      {/* Red strip: only when multi-device, this device isn't in control,
          and someone else clearly holds it. Anchors visually to the red
          beacon button directly above it. */}
      {showBeaconButton && !isController && otherHolderText && (
        <div className="beacon-inactive-strip">{otherHolderText}</div>
      )}
      {tinkNoticeMsg && (
  <div className="tink-alert-strip">
    Your bands will illuminate when Tink flies over the castle!
  </div>
)}

      {/* Sole-holder yellow strip — only when the rare race happens where
          this device is the only known holder and tries to release. */}
      {soleHolderMsg && (
        <div className="beacon-sole-strip">
          At least one device must be in control of the Magic Beacon. Turn on control on another device to switch.
        </div>
      )}
      </div>
      <div className="mb-content" style={showDemo ? { display: "none" } : undefined}>
        <DesignStudio
          ref={designRef}
          device={device}
          onPrepare={handlePrepare}
          onTransmit={handleTransmit}
          onCancel={handleMagicCancel}
        />
      </div>

      {pendingTransmit && hasHolder && holder?.holderDeviceId !== device?._id && (
        <div className="beacon-handoff-backdrop">
          <div className="beacon-handoff-modal">
            <h3>Take Control of Your Magic Beacon?</h3>
            <p>
              Your Wait Time {holder.holderDeviceType}: {holder.holderLabel} is
              currently controlling the beacon. Would you like to switch control
              to this device?
            </p>
            <div className="beacon-handoff-buttons">
              <button className="beacon-handoff-no" onClick={cancelHandoff}>
                No
              </button>
              <button className="beacon-handoff-yes" onClick={confirmHandoffAndTransmit}>
                Yes, Switch
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingTinkOn && hasHolder && holder?.holderDeviceId !== device?._id && (
  <div className="beacon-handoff-backdrop">
    <div className="beacon-handoff-modal">
      <h3>Take Control of Your Magic Beacon?</h3>
      <p>
        Your Wait Time {holder.holderDeviceType}: {holder.holderLabel} is
        currently controlling the beacon. Tink Alert needs control to light
        your bands. Switch control to this device?
      </p>
      <div className="beacon-handoff-buttons">
        <button className="beacon-handoff-no" onClick={cancelTinkTakeControl}>
          No
        </button>
        <button className="beacon-handoff-yes" onClick={confirmTinkTakeControl}>
          Yes, Switch
        </button>
      </div>
    </div>
  </div>
)}
            {lssPopup === "upgrade" && (
        <div className="lss-back" onClick={closeLss}>
          <div className="lss-card" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <h3 className="lss-title"><span className="mb-live-dot" />LIVE Show Sync</h3>
            <p className="lss-sub">VIP Package is required for LIVE Show Sync.</p>
            <button className="lss-upgrade" onClick={goLssUpgrade}>UPGRADE NOW!</button>
            <button className="lss-no" onClick={closeLss}>No thanks</button>
          </div>
        </div>
      )}
      {lssPopup === "pick" && (
        <div className="lss-back" onClick={closeLss}>
          <div className="lss-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="lss-title"><span className="mb-live-dot" />LIVE Show Sync</h3>
            <p className="lss-sub">Pick a LIVE show. Your bands sync to it automatically!</p>
            <div className="lss-opts">
              <button className="lss-opt" onClick={goLssMultiview}>
                <img className="lss-gif" src={LSS_MULTIVIEW_MP4} alt="" draggable={false} />                <span className="lss-opt-name">Magical MultiView</span>
                <span className="lss-opt-desc">Watch LIVE streamers in the parks right now</span>
              </button>
              <button className="lss-opt" onClick={goLssCloseup}>
                <img className="lss-gif" src={LSS_CLOSEUP_MP4} alt="" draggable={false} />                <span className="lss-opt-name">LIVE Close-Up Fireworks</span>
                <span className="lss-opt-desc">Our dedicated fireworks camera, front and center</span>
              </button>
            </div>
              <button className="lss-close" onClick={closeLss}>Close</button>
          </div>
        </div>
      )}    
      {showHowTo && <BandHowTo onClose={() => setShowHowTo(false)} />}
      {showDemo && (
        <BeaconDemo
          device={device}
          isTv={false}
          onExit={() => setShowDemo(false)}
        />
      )}
    </div>
  );
};

export default MagicBand;
